import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Activity, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { getApiErrorMessage } from '../lib/apiErrors';
import { authVerifyEmail } from '../services/api.auth';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    
    if (!token) {
      setStatus('error');
      setMessage('Invalid verification link');
      return;
    }

    verifyEmail(token);
  }, [searchParams]);

  const verifyEmail = async (token) => {
    try {
      const res = await authVerifyEmail(token);
      setStatus('success');
      setMessage(`Email ${res.data.email} verified successfully!`);
    } catch (error) {
      setStatus('error');
      setMessage(getApiErrorMessage(error, 'Verification failed'));
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-[linear-gradient(rgba(59,130,246,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(59,130,246,0.03)_1px,transparent_1px)] bg-[size:50px_50px]" />
      
      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-4">
            <Activity className="w-8 h-8 text-blue-500" />
            <span className="text-2xl font-bold text-white tracking-tight">TradoPro</span>
          </div>
        </div>

        <Card className="bg-zinc-900/80 border-zinc-800 backdrop-blur-xl">
          <CardContent className="pt-8 pb-8">
            <div className="text-center">
              {status === 'verifying' && (
                <>
                  <Loader2 className="w-16 h-16 text-blue-500 animate-spin mx-auto mb-4" />
                  <h2 className="text-xl font-semibold text-white mb-2">Verifying your email...</h2>
                  <p className="text-zinc-400">Please wait a moment</p>
                </>
              )}
              
              {status === 'success' && (
                <>
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h2 className="text-xl font-semibold text-white mb-2">Email Verified!</h2>
                  <p className="text-zinc-400 mb-6">{message}</p>
                  <Button 
                    className="bg-blue-600 hover:bg-blue-700"
                    onClick={() => navigate('/login')}
                  >
                    Go to Sign In
                  </Button>
                </>
              )}
              
              {status === 'error' && (
                <>
                  <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                  <h2 className="text-xl font-semibold text-white mb-2">Verification Failed</h2>
                  <p className="text-zinc-400 mb-6">{message}</p>
                  <div className="space-x-4">
                    <Button 
                      variant="outline"
                      onClick={() => navigate('/login')}
                    >
                      Back to Login
                    </Button>
                    <Button 
                      className="bg-blue-600 hover:bg-blue-700"
                      onClick={() => navigate('/login?tab=register')}
                    >
                      Try Again
                    </Button>
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default VerifyEmail;
